// ignore-string-externalization

import { contentful } from '../lib/contentful';
import { formatShortDate } from '../lib/formatDate';
import { setState } from './root';
import { ResponseError } from './utils/ResponseError';
import { youtubeIdRegex } from '../../../features/utils';
import { getCurrentUTCTimestamp, createUTCDate } from '../../dateUtils';

const categoriesKey = 'blogCategories';
const postsKey = 'blogPosts';

export type LoadBlogPayload = {
  category?: string | null;
  post?: string | null;
  offset?: number | null;
  limit?: number | null;
  smartlingLanguage?: string | null;
  preview?: boolean;
};

/*
  NOTES:
  - related posts are most recent in the same category (excluding the current post)
  - next/previous post are based on release date and in current category
  - recent posts is always from all categories
  - we are always overwriting existing posts in the store to keep content fresh
*/

const categoriesTemplate = [
  { name: 'All', urlSlug: 'all' },
  { name: 'Products', urlSlug: 'products' },
  { name: 'Best Practices', urlSlug: 'best-practices' },
  { name: 'Stories', urlSlug: 'stories' },
  { name: 'Other', urlSlug: 'other' },
];

export const selectBlogCategories = (state: $TSFixMe) => state[categoriesKey];
export const selectBlogPosts = (state: $TSFixMe) => state[postsKey];

const getEmptyCategoriesState = () => ({
  bySlug: categoriesTemplate.reduce(
    (acum, catgry) =>
      Object.assign(acum, {
        [catgry.urlSlug]: { ...catgry, items: [], offset: 0, total: undefined },
      }),
    {},
  ),
  nameToSlug: categoriesTemplate.reduce(
    (acum, catgry) => Object.assign(acum, { [catgry.name]: catgry.urlSlug }),
    {},
  ),
  allSlugs: categoriesTemplate.map(item => item.urlSlug),
});
const getEmptyPostsState = () => ({
  byId: {},
  slugToId: {},
  allSlugs: [],
});

export const extendStateFromJson = (
  oldCategories: $TSFixMe,
  oldPosts: $TSFixMe,
  payloadArray: $TSFixMe,
) => {
  const categories = { ...oldCategories };
  const posts = {
    byId: { ...oldPosts.byId },
    slugToId: {},
    allSlugs: [],
  };
  payloadArray.forEach((json: $TSFixMe) => {
    const postsById = json.items
      .filter(Boolean)
      .reduce((acum: $TSFixMe, item: $TSFixMe) => {
        const {
          category = 'Other',
          featuredVideo,
          headerModule,
          featuredImageFullsize,
          featuredImageLocation,
          releaseDate,
          ...blogPost
        } = item.fields;

        const videoUrlParsed = youtubeIdRegex.exec(featuredVideo);
        const headerModuleId = headerModule && headerModule.sys.id;
        const headerModuleEntry =
          headerModuleId &&
          json.includes.Entry.find(
            (entry: $TSFixMe) => entry.sys.id === headerModuleId,
          );
        const headerModuleImage =
          headerModuleEntry && headerModuleEntry.fields.image;
        const headerModuleImageId =
          headerModuleImage && headerModuleImage.sys.id;
        const asset =
          headerModuleImageId &&
          json.includes.Asset.find(
            (a: $TSFixMe) => a.sys.id === headerModuleImageId,
          ).fields;

        const releaseDateParsed = createUTCDate(releaseDate);

        return Object.assign(acum, {
          [item.sys.id]: {
            ...blogPost,
            id: item.sys.id,
            category,
            body: blogPost.article,
            featuredMediaFullsize: !!featuredImageFullsize,
            featuredMediaDisplayFirst:
              featuredImageLocation !== 'After Introduction',
            featuredVideo: videoUrlParsed && videoUrlParsed[1],
            featuredAsset: asset,
            releaseDate: releaseDateParsed.toISOString(),
            releaseDateUTC: releaseDateParsed.toUTCString(),
            releaseDateFormatted: formatShortDate(releaseDateParsed),
          },
        });
      }, {});

    // merge existing posts with new ones
    posts.byId = {
      ...posts.byId,
      ...postsById,
    };
  });

  // reset category items
  categories.allSlugs.forEach((slug: $TSFixMe) => {
    categories.bySlug[slug].items = [];
  });

  // order all posts and assign to categories
  Object.values(posts.byId)
    .sort((a, b) => {
      if ((a as $TSFixMe).releaseDate === (b as $TSFixMe).releaseDate) {
        return 0;
      }
      return (a as $TSFixMe).releaseDate > (b as $TSFixMe).releaseDate ? -1 : 1;
    })
    .map((item: $TSFixMe) => {
      const categorySlug = categories.nameToSlug[(item as $TSFixMe).category];
      const allCategory = categories.bySlug.all;
      const category = categories.bySlug[categorySlug];
      allCategory.items.push((item as $TSFixMe).id);
      category.items.push(item.id);
      (posts as $TSFixMe).slugToId[item.urlSlug] = item.id;

      return Object.assign(item, { categorySlug });
    });

  posts.allSlugs = categories.bySlug.all.items.map(
    (id: $TSFixMe) => posts.byId[id].urlSlug,
  );

  return {
    [categoriesKey]: categories,
    [postsKey]: posts,
  };
};

export const loadBlog =
  ({
    category = null,
    post: postRaw = null,
    offset = 0,
    limit = 10,
    smartlingLanguage = 'en-US',
    preview = false,
  }: LoadBlogPayload = {}) =>
  async (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();
    const categories = selectBlogCategories(state) || getEmptyCategoriesState();
    const posts = selectBlogPosts(state) || getEmptyPostsState();
    const normalizedCategory =
      category && categories.bySlug[category] ? category : undefined;

    // ensure we URL decode the slugs (for some reason Contentful does not remove apostrophes)
    const post = postRaw ? decodeURIComponent(postRaw) : postRaw;
    const contentfulOptions = Object.assign(
      {
        include: 10,
        limit: post ? 1 : limit,
        skip: post ? 0 : offset,
        content_type: 'articlePage',
        order: '-fields.releaseDate',
        'fields.releaseDate[lte]': getCurrentUTCTimestamp(),
        'fields.requiredFeatureFlag[ne]': 's4a-frodor-test',
        'fields.languages[match]': smartlingLanguage,
      },
      normalizedCategory &&
        normalizedCategory !== 'all' && {
          'fields.category': categories.bySlug[normalizedCategory].name,
        },
      post && { 'fields.urlSlug': post },
    );

    const response = await contentful.getEntries(
      contentfulOptions,
      smartlingLanguage,
      preview,
    );

    if (response.ok) {
      const payload = await response.json();
      let newState = extendStateFromJson(categories, posts, [payload]);

      // if fetching a single category we also get the total number of posts within that category,
      // we want to update the store accordingly to use this info when loading more posts later on
      if (normalizedCategory && !post) {
        newState[categoriesKey].bySlug[normalizedCategory].total =
          payload.total;

        // we want to track how many "pages" we have loaded in case we switch categories in between
        if (offset) {
          newState[categoriesKey].bySlug[normalizedCategory].offset = offset;
        }
      }

      if (post !== undefined) {
        const postId = (newState as $TSFixMe)[postsKey].slugToId[
          post as $TSFixMe
        ];
        const postEntry = newState[postsKey].byId[postId];

        if (postEntry) {
          // 1) fetch previous post in the same category
          // 2) fetch next post in the same category
          // 3) fetch most recent (related) posts in the same category

          const olderPostOptions = {
            ...(contentfulOptions as $TSFixMe),
            skip: 0,
            'fields.urlSlug': undefined,
            'fields.urlSlug[ne]': postEntry.urlSlug,
            locale: smartlingLanguage,
            'fields.category': postEntry.category,
            'fields.releaseDate[lte]': postEntry.releaseDate,
          };

          const newerPostOptions = {
            ...(contentfulOptions as $TSFixMe),
            skip: 0,
            order: 'fields.releaseDate',
            'fields.urlSlug': undefined,
            locale: smartlingLanguage,
            'fields.category': postEntry.category,
            'fields.releaseDate[gt]': postEntry.releaseDate,
          };

          const relatedPostsOptions = {
            ...(contentfulOptions as $TSFixMe),
            skip: 0,
            limit: 3,
            'fields.urlSlug': undefined,
            locale: smartlingLanguage,
            'fields.urlSlug[ne]': postEntry.urlSlug,
            'fields.category': postEntry.category,
          };

          const [olderPost, newerPost, relatedPosts] = await Promise.all([
            contentful.getEntries(olderPostOptions, smartlingLanguage, preview),
            contentful.getEntries(newerPostOptions, smartlingLanguage, preview),
            contentful.getEntries(
              relatedPostsOptions,
              smartlingLanguage,
              preview,
            ),
          ]);

          const payloadArray = [];
          if (olderPost.ok) {
            payloadArray.push(await olderPost.json());
          }
          if (newerPost.ok) {
            payloadArray.push(await newerPost.json());
          }
          if (relatedPosts.ok) {
            payloadArray.push(await relatedPosts.json());
          }

          newState = extendStateFromJson(
            newState[categoriesKey],
            newState[postsKey],
            payloadArray,
          );
        }
      }

      dispatch(
        setState({
          type: `${postsKey}/LOAD`,
          payload: { [postsKey]: newState[postsKey] },
        }),
      );
      dispatch(
        setState({
          type: `${categoriesKey}/LOAD`,
          payload: { [categoriesKey]: newState[categoriesKey] },
        }),
      );
    } else {
      throw new ResponseError(response);
    }
  };
